/**
 * Para onde o clique num caso leva (E78.F1) — regra pura, sem I/O.
 *
 * Antes da E78 a lista mandava todo caso não-rascunho para `/casos/:id/workspace`
 * sem olhar a modalidade. Num caso eletrônico não há assinatura contestada: o
 * servidor respondia `CASE_NOT_READY_FOR_WORKSPACE` e a tela caía no wizard,
 * cujo gate exige extração `completed` — e a raia eletrônica marca `skipped`.
 * O caso concluído ficava inalcançável a não ser digitando a URL do laudo.
 *
 * A decisão vive aqui, e não dentro da página, porque é a regra que precisa de
 * teste nos quatro estados; a página só carrega os dados e navega.
 */
import type { CaseRecordData } from '@/lib/cases';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';

/**
 * As telas por onde um caso pode começar. E201.F1 (GUS-47, ADR-0144): a pré-análise e a mesa do
 * caso eletrônico viraram a mesa única (`mesa`).
 */
export type DestinoDoCaso = 'wizard' | 'mesa' | 'workspace' | 'laudo';

const ROTA: Record<DestinoDoCaso, (caseId: string) => string> = {
  wizard: (id) => `/casos/${id}/wizard`,
  mesa: (id) => `/casos/${id}/mesa`,
  workspace: (id) => `/casos/${id}/workspace`,
  laudo: (id) => `/casos/${id}/laudo`,
};

export function rotaDoDestino(destino: DestinoDoCaso, caseId: string): string {
  return ROTA[destino](caseId);
}

/**
 * E209.F3.T01 (GUS-296 item 11, GT-2026-0335): o caso que aguarda arquivos (o `content.json`, o
 * `.tst` ou um documento declarado). Ele abre na mesa, que diz o que falta e onde enviar; o
 * assistente antigo oferecia "Concluir e ir para a análise preliminar" e contava os códigos por conta
 * própria.
 */
export function aguardaArquivos(
  caso: Pick<CaseRecordData, 'workflow_status' | 'arquivos_pendentes'>,
): boolean {
  return (
    caso.workflow_status === 'aguardando_arquivos' || (caso.arquivos_pendentes?.length ?? 0) > 0
  );
}

/** E209.F3.T01: onde o caso que aguarda arquivos recebe o que falta. */
export function rotaDoEnvioNaMesa(caseId: string): string {
  return `/casos/${caseId}/mesa#adicionar-documentos`;
}

/**
 * Destino de um caso da raia eletrônica, pelo estado das peças.
 *
 * `pecas` é a lista da raia (uma entrada por CCB contestada viva). A ordem das
 * perguntas é a do trabalho: primeiro o que já acabou, depois o que está na
 * mesa, e por último o que ainda nem foi verificado.
 */
export function destinoEletronico(
  caso: CaseRecordData,
  pecas: readonly ExameEletronicoCcbData[],
): DestinoDoCaso {
  // Rascunho retoma na preparação: o caso eletrônico só sai de `draft` na
  // primeira verificação, então quem parou antes de processar volta pelo
  // "Concluir" do assistente — um clique, e sem o risco de tirar da preparação
  // quem ainda está anexando documentos. O caso do portal com peça é exceção
  // (E168.F7.C02, GUS-180): quem anexa é o cliente, que já concluiu o envio, e
  // a pré-análise processa sozinha — o assistente só mandava para lá.
  if (caso.status === 'draft') {
    // E209.F3.T01: o caso que aguarda arquivos abre na mesa, com ou sem banco cadastrado.
    if (aguardaArquivos(caso)) return 'mesa';
    // E215.F2 (GUS-315, reteste de 01/11, GT-2026-0340): com peça, o envio foi concluído (a
    // abertura separa as CCBs na conclusão) e o caso está sendo lido e preparado; ele abre na mesa,
    // com ou sem banco — o do escritório só ganha o banco quando a CCB é lida.
    return pecas.length > 0 ? 'mesa' : 'wizard';
  }
  // Homologado é terminal: o documento existe e foi aprovado, ainda que uma
  // peça tenha sido reaberta depois.
  if (caso.review_status === 'approved') return 'laudo';
  // E123.F3 (GUS-128): a etapa fotográfica saiu da esteira. E201.F1 (GUS-47): a pré-análise e a
  // mesa são uma tela só; o caso com tudo concluído abre o laudo.
  return etapaAlcancadaEletronica(pecas) === 'laudo' ? 'laudo' : 'mesa';
}

/**
 * Etapa mais avançada que o caso ALCANÇOU, pelas peças (E84.F5, RD-01/GUS-40).
 *
 * É o núcleo que `destinoEletronico` sempre usou, agora exportado — porque a
 * esteira precisava da mesma resposta e derivava a sua do `activeStage` da
 * página. Na pré-análise isso queria dizer "alcançou a pré-análise", então
 * **Mesa ficava `upcoming` para sempre**, enquanto a lista de casos mandava a
 * mesma pessoa direto para a mesa. Duas regras para a mesma pergunta.
 *
 * Uma função só, e as duas telas a chamam: elas não têm como discordar.
 */
export function etapaAlcancadaEletronica(
  pecas: readonly ExameEletronicoCcbData[],
): 'preliminar' | 'mesa' | 'laudo' {
  const concluidas = pecas.filter((p) => p.exame?.status === 'concluido').length;
  if (pecas.length > 0 && concluidas === pecas.length) return 'laudo';
  return podeIrParaMesaEletronica(pecas) ? 'mesa' : 'preliminar';
}

/**
 * Alguma peça já foi verificada? É o que habilita a ida à mesa (GUS-40).
 *
 * Deliberadamente NÃO exige código preenchido em todas as peças nem ausência
 * de divergência: a mesa é onde o perito examina justamente o que divergiu.
 * Exigir o caso limpo para chegar lá tornaria a mesa inalcançável nos casos
 * que ela existe para tratar.
 */
export function podeIrParaMesaEletronica(pecas: readonly ExameEletronicoCcbData[]): boolean {
  return pecas.some((p) => p.exame?.status === 'verificado' || p.exame?.status === 'concluido');
}

/**
 * Destino de um caso grafotécnico — o comportamento de hoje, preservado.
 *
 * A E78.F1 não mexe na esteira visual: rascunho abre a preparação do caso, o
 * resto abre a mesa. O que muda é o caminho até aqui (a lista deixou de decidir
 * a rota) e não a decisão.
 */
export function destinoGrafotecnico(caso: CaseRecordData): DestinoDoCaso {
  return caso.status === 'draft' ? 'wizard' : 'workspace';
}

export function destinoDoCaso(
  caso: CaseRecordData,
  pecas: readonly ExameEletronicoCcbData[],
): DestinoDoCaso {
  return caso.modalidade === 'eletronica'
    ? destinoEletronico(caso, pecas)
    : destinoGrafotecnico(caso);
}
